import "./Employees.css";
import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  Form,
  Select,
  Input,
  Button,
  Table,
  Modal,
  Tag,
  Radio,
  Col,
  Row,
  Space,
  Drawer,
  Popconfirm,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  TeamOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  getEmployees,
  getBranches,
  createEmployee,
  updateEmployee,
  activateEmployee,
  deactivateEmployee,
  getSubscriptions,
  generateLicenseInvoice,
  verifyPayment,
  deleteEmployee,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import { AddonName, PlanType, UserType } from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import HyperPayPaymentModal from "../../../components/HyperPayPaymentModal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ImageUpload from "../../../components/ImageUpload";
import QuantityStepper from "../../../components/QuantityStepper";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import { applyAttachmentToPayload } from "../../../utils/attachmentPayload";

const EMPLOYEE_PHOTO_EXTENSIONS = ["jpeg", "png", "jpg", "gif", "svg"];

const Employees = () => {
  const { authUser, setAuthUser, appConfig, setFullPageLoading } =
    useContext(AppContext);
  const ADD_EMPLOYEE = "Add Employee";

  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerData, setDrawerData] = useState([]);
  const [addEmployeeDrawer, setAddEmployeeDrawer] = useState(false);
  const [editEmployeeDrawer, setEditEmployeeDrawer] = useState(false);
  const [drawerTitle, SetDrawerTitle] = useState(ADD_EMPLOYEE);
  const [addLicenseModalVisible, setAddLicenseModalVisible] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [validatePassword, setValidatePassword] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState(false);
  const [freeTrialOver, setFreeTrialOver] = useState(false);
  const [usersCount, setUsersCount] = useState(1);
  const [branches, setBranches] = useState([]);
  const [employeesTableData, setEmployeesTableData] = useState([]);
  const [invoice, setInvoice] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [employeeForm] = Form.useForm();
  const [addLicenseForm] = Form.useForm();
  const [photo, setPhoto] = useState(false);

  const { t } = useTranslation();

  const is_waiter_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.WaiterManagement,
  );
  const is_job_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.JobManagement,
  );
  const is_stock_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );
  const is_customer_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.CustomerManagement,
  );
  const is_sales_invoice_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.A4SalesInvoice,
  );
  const is_ticketing_addon_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.Ticketing,
  );
  const is_zatca_e_invoicing_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.ZatcaEInvoicing,
  );

  // Fetch employees + subscription
  useEffect(() => {
    setIsDataLoading(true);
    getEmployees()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setEmployeesTableData(
          response.data.data.owner.concat(response.data.data.employees),
        );
        getSubscriptions()
          .then((response) => {
            if (response?.data?.success === false) {
              handleApiError(response, t);
              return;
            }

            setActiveSubscription(response.data.data.active_subscription);
            setFreeTrialOver(response.data.data.is_free_trial_over);
          })
          .catch((error) => handleApiError(error, t));
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  useEffect(() => {
    setIsDataLoading(true);
  }, [reloadTableData]);

  // HyperPay callback handler
  useEffect(() => {
    if (window.location.search !== "") {
      let params = new URLSearchParams(window.location.search);
      if (params.get("id")) {
        setFullPageLoading(true);
        setTimeout(function () {
          verifyPayment({ checkoutID: params.get("id") })
            .then((response) => {
              if (response?.data?.success === false) {
                handleApiError(response, t);
                return;
              }

              if (
                response.data &&
                response.data &&
                response.data.result &&
                response.data.result.code
              ) {
                if (response.data.result.code.split(".")[0] === "000") {
                  window.location =
                    window.location.pathname + "?payment_successful=1";
                } else {
                  window.location =
                    window.location.pathname + "?payment_successful=0";
                }
              } else {
                handleApiError(response, t);
              }
            })
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              setFullPageLoading(false);
            });
        }, 1000);
      } else if (params.get("payment_successful")) {
        if (params.get("payment_successful") === "1") {
          NotificationWithIcon(
            "success",
            t("payment_successful"),
            t("payment_successful"),
          );
        } else {
          showAppError(t("payment_failed"));
        }
      }
    }
  }, [setFullPageLoading, t]);

  // Branches fetch
  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranches(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  // Open Add-Employee drawer
  const showAddModal = () => {
    setAddEmployeeDrawer(true);
    setEditEmployeeDrawer(false);
    SetDrawerTitle(t("employee_add"));

    employeeForm.setFieldsValue({
      allow_editable_price: false,
      allow_discount: false,
      can_see_transactions: false,
      can_refund_transaction: false,
      is_machine_user: false,
      can_add_edit_product: false,
      is_waiter: false,
      can_request_stock_transfer: false,
      can_approve_stock_transfer: false,
      can_request_stock_adjustment: false,
      can_add_edit_customer: false,
      can_view_customer: false,
      can_add_pay_sales_invoice: false,
      can_view_sales_invoice: false,
      can_scan_view_ticket: false,
      can_manage_zatca: false,
      nationality: undefined,
      id_number: undefined,
    });

    setPhoto(false);
    setDrawerVisible(true);
    setValidatePassword(true);
  };

  // Handle API response for add/update employee
  const handleAPIResponse = (response, type) => {
    let data = response.data || response.response?.data;

    if (data?.success === true) {
      const updatedEmployee = data?.data?.employee || data?.data?.owner || null;

      if (updatedEmployee && updatedEmployee.id === authUser.id) {
        setAuthUser((prev) => ({
          ...prev,
          ...updatedEmployee,
        }));
      }

      setReloadTableData((prev) => !prev);

      if (type === ADD_EMPLOYEE) {
        NotificationWithIcon(
          "success",
          t("employee_added"),
          t("employee_added_successfully"),
        );
      } else {
        NotificationWithIcon(
          "success",
          t("employee_updated"),
          t("employee_updated_successfully"),
        );
      }

      employeeForm.resetFields();
      setPhoto(false);
      setDrawerVisible(false);
      setEditEmployeeDrawer(false);
      setAddEmployeeDrawer(false);
    } else if (data?.success === false) {
      handleApiError(response, t);
    }
  };

  // Add / Edit employee (submit drawer form)
  const handleAddEditEmployee = () => {
    employeeForm
      .validateFields()
      .then((values) => {
        const formData = new window.FormData();

        applyAttachmentToPayload(formData, "photo", photo);

        Object.keys(values).forEach((key) => {
          let value = values[key];

          if (value === undefined || value === null) {
            return;
          }

          if (typeof value === "boolean") {
            value = value ? 1 : 0;
          }

          formData.append(key, value);
        });

        setLoading(true);

        if (drawerTitle === ADD_EMPLOYEE || drawerTitle === t("employee_add")) {
          createEmployee(formData)
            .then((response) => handleAPIResponse(response, ADD_EMPLOYEE))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              setLoading(false);
            });
        } else {
          updateEmployee(formData)
            .then((response) => handleAPIResponse(response, null))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              setLoading(false);
            });
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  // View drawer in read-only mode
  const handleViewDrawer = (employee) => {
    SetDrawerTitle(employee.name);
    setDrawerData(employee);
    setDrawerVisible(true);
    setEditEmployeeDrawer(false);
    setAddEmployeeDrawer(false);
  };

  // Edit drawer setup
  const handleEditModal = (employee) => {
    setEditEmployeeDrawer(true);
    employeeForm.resetFields();

    employeeForm.setFieldsValue({
      id: employee.id,
      first_name: employee.first_name,
      last_name: employee.last_name,
      phone: employee.phone,
      branch_id: employee.branch_id,
      allow_editable_price: !!employee.allow_editable_price,
      allow_discount: !!employee.allow_discount,
      can_see_transactions: !!employee.can_see_transactions,
      can_refund_transaction: !!employee.can_refund_transaction,
      is_machine_user: !!employee.is_machine_user,
      can_add_edit_product: !!employee.can_add_edit_product,
      is_waiter: !!employee.is_waiter,
      can_request_stock_transfer: !!employee.can_request_stock_transfer,
      can_approve_stock_transfer: !!employee.can_approve_stock_transfer,
      can_request_stock_adjustment: !!employee.can_request_stock_adjustment,
      can_add_edit_customer: !!employee.can_add_edit_customer,
      can_view_customer: !!employee.can_view_customer,
      can_add_pay_sales_invoice: !!employee.can_add_pay_sales_invoice,
      can_view_sales_invoice: !!employee.can_view_sales_invoice,
      can_scan_view_ticket: !!employee.can_scan_view_ticket,
      can_manage_zatca: !!employee.can_manage_zatca,
      nationality: employee.nationality || undefined,
      id_number: employee.id_number || undefined,
    });

    if (employee.photo) {
      setPhoto(employee.photo);
    } else {
      setPhoto(false);
    }

    setValidatePassword(false);
  };

  const handleCancel = () => {
    employeeForm.resetFields();
    setDrawerVisible(false);
    setEditEmployeeDrawer(false);
    setAddEmployeeDrawer(false);
    setDrawerData([]);
    setPhoto(false);
  };

  const handleDelete = (id) => {
    deleteEmployee(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          handleCancel();
          NotificationWithIcon(
            "success",
            t("employee_deleted"),
            t("employee_deleted_successfully"),
          );
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const passwordValueChanged = (e) => {
    if (
      e.target.value.length === 0 &&
      !(drawerTitle === ADD_EMPLOYEE || drawerTitle === t("employee_add"))
    ) {
      setValidatePassword(false);
    } else {
      setValidatePassword(true);
    }
  };

  const handleActivate = (employee_id) => {
    activateEmployee(employee_id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          NotificationWithIcon(
            "success",
            t("employee_activated"),
            t("employee_activated_successfully"),
          );
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const handleDeactivate = (employee_id) => {
    deactivateEmployee(employee_id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          NotificationWithIcon(
            "success",
            t("employee_deactivated"),
            t("employee_deactivated_successfully"),
          );
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const showBuyMoreLicenseNotification = () => {
    showAppError(t("buy_more_license_error"));
  };

  const showAddLicenseModal = () => {
    setUsersCount(1);
    setAddLicenseModalVisible(true);
  };

  const handleAddLicense = () => {
    generateLicenseInvoice({ users_count: usersCount })
      .then((response) => {
        if (response.data.success === true) {
          setInvoice(response.data.data.invoice);
          setPaymentModalVisible(true);
          setReloadTableData(!reloadTableData);
          NotificationWithIcon(
            "success",
            t("user_licenses_added"),
            t("user_licenses_have_been_added_successfully"),
          );
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setAddLicenseModalVisible(false);
      });
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "employees",
      breadcrumbName: t("employees"),
      icon: <TeamOutlined />,
    },
  ];

  // TABLE COLUMNS (with new photo column)
  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => <>{index + 1}</>,
    },
    {
      title: t("photo"),
      render: (employee) => (
        <img
          alt={employee.name}
          src={employee.photo ? employee.photo : "/images/image-not-found.png"}
          style={{
            width: "50px",
            height: "50px",
            objectFit: "cover",
            borderRadius: "1rem",
            border: "1px solid var(--color-border)",
          }}
        />
      ),
    },
    {
      title: t("name"),
      dataIndex: "name",
    },
    {
      title: t("phone_number"),
      dataIndex: "phone",
    },
    {
      title: t("branch"),
      align: "center",
      dataIndex: "branch_name",
    },
    {
      title: t("type"),
      align: "center",
      render: (record) => (
        <>
          {record.type === UserType.BusinessOwner ? (
            <Tag color="green">{t("owner")}</Tag>
          ) : (
            <Tag color="blue">{t("employee")}</Tag>
          )}
          {(is_waiter_management_active || is_job_management_active) &&
            record.is_waiter && (
              <Tag style={{ marginTop: "5px" }} color="orange">
                {is_waiter_management_active ? t("waiter") : t("job_person")}
              </Tag>
            )}
        </>
      ),
    },
    {
      title: t("status"),
      align: "center",
      render: (employee) => (
        <>
          {employee.is_active ? (
            <Button
              type="link"
              onClick={() =>
                employee.type !== UserType.BusinessOwner &&
                handleDeactivate(employee.id)
              }
              style={{ fontSize: "18px", color: "var(--color-success-hover)" }}
            >
              <UnlockOutlined title={t("active")} />
            </Button>
          ) : (
            <Button
              type="link"
              onClick={() =>
                employee.type !== UserType.BusinessOwner &&
                handleActivate(employee.id)
              }
              style={{ fontSize: "18px", color: "var(--color-danger)" }}
            >
              <LockOutlined title={t("inactive")} />
            </Button>
          )}
        </>
      ),
    },
  ];

  const actionPartColumn = [
    {
      title: t("action"),
      align: "center",
      width: 150,
      render: (employee) => (
        <>
          <Space>
            <Button
              type="link"
              key="View"
              title={t("view_details")}
              onClick={() => handleViewDrawer(employee)}
            >
              <EyeOutlined />
            </Button>
            <Button
              type="link"
              key="edit"
              onClick={() => {
                setDrawerVisible(true);
                SetDrawerTitle(employee.name);
                handleEditModal(employee);
              }}
              title={t("edit")}
            >
              <EditOutlined />
            </Button>

            {employee.type !== UserType.BusinessOwner && (
              <Popconfirm
                title={t("warning_delete")}
                onConfirm={() => handleDelete(employee.id)}
                okText={t("yes")}
                cancelText={t("no")}
                placement="topLeft"
              >
                <Button type="link" title={t("delete_entity")}>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            )}
          </Space>
        </>
      ),
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("employees")}
          ghost={false}
          extra={[
            activeSubscription && (
              <div key={1}>
                {t("licenses_used_summary", {
                  used: activeSubscription.used_users_count,
                  total: activeSubscription.user_licenses_count,
                })}
              </div>
            ),
            activeSubscription &&
              activeSubscription.used_users_count !==
                activeSubscription.user_licenses_count && (
                <Button
                  type="default"
                  key="open-add-employee-modal"
                  className="btn-success"
                  onClick={
                    activeSubscription.used_users_count <
                    activeSubscription.user_licenses_count
                      ? showAddModal
                      : showBuyMoreLicenseNotification
                  }
                >
                  + {t("employee_add")}
                </Button>
              ),
            activeSubscription && (
              <Button
                key="open-add-license-modal"
                type="primary"
                onClick={showAddLicenseModal}
              >
                +{" "}
                {activeSubscription.is_trial &&
                activeSubscription.type === PlanType.Pro &&
                freeTrialOver === false
                  ? t("add_free_license")
                  : t("add_license")}
              </Button>
            ),
          ]}
        />
      </Card>

      <Card>
        <Table
          columns={[...detailsPartColumns, ...actionPartColumn]}
          dataSource={employeesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* Add / Edit Employee Drawer */}
      <Drawer
        title={drawerTitle}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={handleCancel}
        open={drawerVisible}
        width={1000}
        closable={false}
        footer={
          editEmployeeDrawer || addEmployeeDrawer ? (
            <Space>
              {editEmployeeDrawer && (
                <Button
                  type="default"
                  key="back"
                  loading={loading}
                  onClick={handleCancel}
                >
                  {t("cancel")}
                </Button>
              )}
              <Button
                key="submit"
                type="primary"
                loading={loading}
                onClick={handleAddEditEmployee}
              >
                {drawerTitle === ADD_EMPLOYEE ||
                drawerTitle === t("employee_add")
                  ? t("employee_add")
                  : t("update")}
              </Button>
            </Space>
          ) : (
            drawerData.type !== UserType.BusinessOwner && (
              <Space>
                <Button
                  type="primary"
                  title={t("edit")}
                  onClick={() => handleEditModal(drawerData)}
                >
                  <EditOutlined />
                </Button>
                <Popconfirm
                  title={t("warning_delete")}
                  onConfirm={() => handleDelete(drawerData.id)}
                  okText={t("yes")}
                  cancelText={t("no")}
                  placement="topLeft"
                >
                  <Button type="text" danger title={t("delete_entity")}>
                    <DeleteOutlined />
                  </Button>
                </Popconfirm>
              </Space>
            )
          )
        }
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setDrawerVisible(false);
            }}
          />
        }
      >
        {editEmployeeDrawer || addEmployeeDrawer ? (
          <Form
            name="add-employee-form"
            layout="vertical"
            onFinish={handleAddEditEmployee}
            className="app-form-full"
            form={employeeForm}
            autoComplete="off"
          >
            <Row gutter={[20, 20]}>
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <div className="ant-col ant-form-item-label">
                  <label title={t("photo")}>{t("photo")}</label>
                </div>
                <ImageUpload
                  name="employee_photo"
                  callbackSetImageFile={setPhoto}
                  image={photo}
                  allowedExtensions={EMPLOYEE_PHOTO_EXTENSIONS}
                  fileTypeErrorKey="validate_photo_file_type"
                />
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="first_name"
                  label={t("first_name")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_name_required"),
                      whitespace: true,
                    },
                    {
                      min: 3,
                      message: t("validate_name_min_characters"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input
                    autoComplete="off"
                    name="first_name"
                    readOnly
                    onFocus={(e) => {
                      e.target.removeAttribute("readonly");
                    }}
                  />
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="last_name"
                  label={t("last_name")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_name_required"),
                      whitespace: true,
                    },
                    {
                      min: 3,
                      message: t("validate_name_min_characters"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input
                    autoComplete="off"
                    name="last_name"
                    readOnly
                    onFocus={(e) => {
                      e.target.removeAttribute("readonly");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item name="nationality" label={t("nationality")}>
                  <Input
                    autoComplete="off"
                    name="nationality"
                    readOnly
                    onFocus={(e) => {
                      e.target.removeAttribute("readonly");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item name="id_number" label={t("id_number")}>
                  <Input
                    autoComplete="off"
                    name="id_number"
                    readOnly
                    onFocus={(e) => {
                      e.target.removeAttribute("readonly");
                    }}
                  />
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="phone"
                  label={t("phone_number") + " eg: +966 5xxxxxxx"}
                  rules={[
                    {
                      required: true,
                      message: t("validate_phone_required"),
                    },
                    {
                      min: 12,
                      message: t("validate_phone_exact_limit"),
                    },
                    {
                      max: 12,
                      message: t("validate_phone_exact_limit"),
                    },
                  ]}
                >
                  <PhoneInput
                    localization={["sa"]}
                    masks={{ sa: "(...) ..-..-.." }}
                    placeholder="+966 (123) 45-67-89"
                    country={"sa"}
                    onlyCountries={["sa"]}
                    style={{
                      width: "100%",
                      direction: "ltr",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="branch_id"
                  label={t("branch")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_branch_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    options={branches.map((type) => ({
                      label: type.name,
                      value: type.id,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="password"
                  label={t("password")}
                  rules={
                    validatePassword && [
                      {
                        required: true,
                        message: t("validate_password_required"),
                      },
                      {
                        min: 6,
                        message: t("validate_password_min_characters"),
                      },
                    ]
                  }
                  hasFeedback
                >
                  <Input.Password onChange={passwordValueChanged} />
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="password_confirmation"
                  label={t("confirm_password")}
                  dependencies={["password"]}
                  hasFeedback
                  rules={
                    validatePassword && [
                      {
                        required: true,
                        message: t("validate_password_confirm_not_matched"),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              t("validate_password_confirm_not_matched"),
                            ),
                          );
                        },
                      }),
                    ]
                  }
                >
                  <Input.Password />
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="allow_editable_price"
                  label={t("user_can_edit_price_on_pos")}
                >
                  <Radio.Group buttonStyle="solid" key="allow-editable-price">
                    <Radio.Button
                      key="allow-editable-price-0"
                      value={false}
                      className="button_no"
                    >
                      {t("no")}
                    </Radio.Button>
                    <Radio.Button
                      key="allow-editable-price-1"
                      value={true}
                      className="button_yes"
                    >
                      {t("yes")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="allow_discount"
                  label={t("user_can_apply_discount")}
                >
                  <Radio.Group buttonStyle="solid" key="allow-discount">
                    <Radio.Button
                      key="allow-discount-0"
                      value={false}
                      className="button_no"
                    >
                      {t("no")}
                    </Radio.Button>
                    <Radio.Button
                      key="allow-discount-1"
                      value={true}
                      className="button_yes"
                    >
                      {t("yes")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="can_see_transactions"
                  label={t("user_see_transactions")}
                >
                  <Radio.Group buttonStyle="solid" key="see-transactions">
                    <Radio.Button
                      key="see-transactions-0"
                      value={false}
                      className="button_no"
                    >
                      {t("no")}
                    </Radio.Button>
                    <Radio.Button
                      key="see-transactions-1"
                      value={true}
                      className="button_yes"
                    >
                      {t("yes")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="can_refund_transaction"
                  label={t("user_can_refund_transaction")}
                >
                  <Radio.Group buttonStyle="solid" key="allow-refund">
                    <Radio.Button
                      key="allow-refund-0"
                      value={false}
                      className="button_no"
                    >
                      {t("no")}
                    </Radio.Button>
                    <Radio.Button
                      key="allow-refund-1"
                      value={true}
                      className="button_yes"
                    >
                      {t("yes")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="can_add_edit_product"
                  label={t("user_can_add_edit_products")}
                >
                  <Radio.Group buttonStyle="solid" key="allow-add-product">
                    <Radio.Button
                      key="allow-add-product-0"
                      value={false}
                      className="button_no"
                    >
                      {t("no")}
                    </Radio.Button>
                    <Radio.Button
                      key="allow-add-product-1"
                      value={true}
                      className="button_yes"
                    >
                      {t("yes")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              {(is_waiter_management_active || is_job_management_active) && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="is_waiter"
                    label={
                      is_waiter_management_active
                        ? t("is_waiter")
                        : t("is_job_person")
                    }
                  >
                    <Radio.Group buttonStyle="solid" key="is_waiter">
                      <Radio.Button
                        key="is_waiter-0"
                        value={false}
                        className="button_no"
                      >
                        {t("no")}
                      </Radio.Button>
                      <Radio.Button
                        key="is_waiter-1"
                        value={true}
                        className="button_yes"
                      >
                        {t("yes")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}

              {is_stock_management_active && (
                <>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_request_stock_transfer"
                      label={t("user_can_request_stock_transfer")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-request-stock-transfer"
                      >
                        <Radio.Button
                          key="user-can-request-stock-transfer-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-request-stock-transfer-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_approve_stock_transfer"
                      label={t("user_can_approve_stock_transfer")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-approve-stock-transfer"
                      >
                        <Radio.Button
                          key="user-can-approve-stock-transfer-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-approve-stock-transfer-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_request_stock_adjustment"
                      label={t("user_allowed_stock_adjustment")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-request-stock-adjustment"
                      >
                        <Radio.Button
                          key="user-can-request-stock-adjustment-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-request-stock-adjustment-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </>
              )}

              {is_customer_management_active && (
                <>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_add_edit_customer"
                      label={t("user_can_add_edit_customers")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-add-edit-customer"
                        onChange={(e) =>
                          e.target.value
                            ? employeeForm.setFieldsValue({
                                can_view_customer: e.target.value,
                              })
                            : ""
                        }
                      >
                        <Radio.Button
                          key="user-can-add-edit-customer-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-add-edit-customer-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_view_customer"
                      label={t("user_can_view_customers")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-view-customer"
                      >
                        <Radio.Button
                          key="user-can-view-customer-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-view-customer-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </>
              )}

              {is_sales_invoice_active && (
                <>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_add_pay_sales_invoice"
                      label={t("user_can_add_pay_sales_invoices")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-add-pay-sales-invoices"
                        onChange={(e) =>
                          e.target.value
                            ? employeeForm.setFieldsValue({
                                can_view_sales_invoice: e.target.value,
                              })
                            : ""
                        }
                      >
                        <Radio.Button
                          key="user-can-add-pay-sales-invoices-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-add-pay-sales-invoices-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                  >
                    <Form.Item
                      name="can_view_sales_invoice"
                      label={t("user_can_view_sales_invoices")}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        key="user-can-view-sales-invoice"
                      >
                        <Radio.Button
                          key="user-can-view-sales-invoice-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="user-can-view-sales-invoice-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </>
              )}

              {is_ticketing_addon_active && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="can_scan_view_ticket"
                    label={t("user_can_scan_view_ticket")}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      key="user-can-scan-view-ticket"
                    >
                      <Radio.Button
                        key="user-can-scan-view-ticket-0"
                        value={false}
                        className="button_no"
                      >
                        {t("no")}
                      </Radio.Button>
                      <Radio.Button
                        key="user-can-scan-view-ticket-1"
                        value={true}
                        className="button_yes"
                      >
                        {t("yes")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}

              {is_zatca_e_invoicing_active && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="can_manage_zatca"
                    label={t("user_can_manage_zatca")}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      key="user-can-manage-zatca"
                    >
                      <Radio.Button
                        key="user-can-manage-zatca-0"
                        value={false}
                        className="button_no"
                      >
                        {t("no")}
                      </Radio.Button>
                      <Radio.Button
                        key="user-can-manage-zatca-1"
                        value={true}
                        className="button_yes"
                      >
                        {t("yes")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}

              {activeSubscription &&
                activeSubscription.is_trial === 1 &&
                activeSubscription.type === PlanType.Pro &&
                activeSubscription.validity_days > 90 && (
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={12}
                  >
                    <Form.Item
                      name="is_machine_user"
                      label={t("is_machine_user")}
                    >
                      <Radio.Group buttonStyle="solid" key="is-machine-user">
                        <Radio.Button
                          key="is-machine-user-0"
                          value={false}
                          className="button_no"
                        >
                          {t("no")}
                        </Radio.Button>
                        <Radio.Button
                          key="is-machine-user-1"
                          value={true}
                          className="button_yes"
                        >
                          {t("yes")}
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                )}

              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <Form.Item name="id" className="app-hidden">
                  <input type="hidden" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        ) : (
          <Row gutter={[20, 20]}>
            {drawerData.photo && (
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={8}
                lg={8}
                xl={6}
                xxl={6}
              >
                <div className="app-detail-label">{t("photo")}</div>
                <div
                  style={{
                    border: "1px solid var(--color-border)",
                    padding: "10px",
                    textAlign: "center",
                  }}
                >
                  <img
                    alt={drawerData.name}
                    src={
                      drawerData.photo
                        ? drawerData.photo
                        : "/images/image-not-found.png"
                    }
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </Col>
            )}

            {drawerData.first_name && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("first_name")}</div>
                <div className="app-detail-value">{drawerData.first_name}</div>
              </Col>
            )}

            {drawerData.last_name && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("last_name")}</div>
                <div className="app-detail-value">{drawerData.last_name}</div>
              </Col>
            )}

            {drawerData.nationality && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("nationality")}</div>
                <div className="app-detail-value">{drawerData.nationality}</div>
              </Col>
            )}

            {drawerData.id_number && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("id_number")}</div>
                <div className="app-detail-value">{drawerData.id_number}</div>
              </Col>
            )}

            {drawerData.phone && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("phone_number")}</div>
                <div className="app-detail-value">{drawerData.phone}</div>
              </Col>
            )}

            {drawerData.branch_name && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("branch")}</div>
                <div className="app-detail-value">{drawerData.branch_name}</div>
              </Col>
            )}

            {drawerData.type && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">{t("type")}</div>
                <div className="app-detail-value-compact">
                  {drawerData.type === UserType.BusinessOwner ? (
                    <Tag color="green">{t("owner")}</Tag>
                  ) : (
                    <Tag color="blue">{t("employee")}</Tag>
                  )}
                  {(is_waiter_management_active || is_job_management_active) &&
                    drawerData.is_waiter && (
                      <Tag color="orange">
                        {is_waiter_management_active
                          ? t("waiter")
                          : t("job_person")}
                      </Tag>
                    )}
                </div>
              </Col>
            )}

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">{t("status")}</div>
              <div className="app-detail-value-compact">
                {drawerData.is_active ? (
                  <Tag color="green">{t("active")}</Tag>
                ) : (
                  <Tag color="red">{t("inactive")}</Tag>
                )}
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">{t("is_machine_user")}</div>
              <div className="app-detail-value-compact">
                {drawerData.is_machine_user ? (
                  <Tag color="green">{t("yes")}</Tag>
                ) : (
                  <Tag color="red">{t("no")}</Tag>
                )}
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">
                {t("user_can_edit_price_on_pos")}
              </div>
              <div className="app-detail-value-compact">
                {drawerData.allow_editable_price ? (
                  <Tag color="green">{t("yes")}</Tag>
                ) : (
                  <Tag color="red">{t("no")}</Tag>
                )}
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">
                {t("user_can_apply_discount")}
              </div>
              <div className="app-detail-value-compact">
                {drawerData.allow_discount ? (
                  <Tag color="green">{t("yes")}</Tag>
                ) : (
                  <Tag color="red">{t("no")}</Tag>
                )}
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">
                {t("user_see_transactions")}
              </div>
              <div className="app-detail-value-compact">
                {drawerData.can_see_transactions ? (
                  <Tag color="green">{t("yes")}</Tag>
                ) : (
                  <Tag color="red">{t("no")}</Tag>
                )}
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">
                {t("user_can_refund_transaction")}
              </div>
              <div className="app-detail-value-compact">
                {drawerData.can_refund_transaction ? (
                  <Tag color="green">{t("yes")}</Tag>
                ) : (
                  <Tag color="red">{t("no")}</Tag>
                )}
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              xxl={8}
            >
              <div className="app-detail-label">
                {t("user_can_add_edit_products")}
              </div>
              <div className="app-detail-value-compact">
                {drawerData.can_add_edit_product ? (
                  <Tag color="green">{t("yes")}</Tag>
                ) : (
                  <Tag color="red">{t("no")}</Tag>
                )}
              </div>
            </Col>

            {is_stock_management_active && (
              <>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_can_approve_stock_transfer")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_approve_stock_transfer ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_can_request_stock_transfer")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_request_stock_transfer ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_allowed_stock_adjustment")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_request_stock_adjustment ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>
              </>
            )}

            {is_customer_management_active && (
              <>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_can_add_edit_customers")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_add_edit_customer ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_can_view_customers")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_view_customer ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>
              </>
            )}

            {is_sales_invoice_active && (
              <>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_can_add_pay_sales_invoices")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_add_pay_sales_invoice ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">
                    {t("user_can_view_sales_invoices")}
                  </div>
                  <div className="app-detail-value-compact">
                    {drawerData.can_view_sales_invoice ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>
              </>
            )}

            {is_ticketing_addon_active && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">
                  {t("user_can_scan_view_ticket")}
                </div>
                <div className="app-detail-value-compact">
                  {drawerData.can_scan_view_ticket ? (
                    <Tag color="green">{t("yes")}</Tag>
                  ) : (
                    <Tag color="red">{t("no")}</Tag>
                  )}
                </div>
              </Col>
            )}

            {is_zatca_e_invoicing_active && (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <div className="app-detail-label">
                  {t("user_can_manage_zatca")}
                </div>
                <div className="app-detail-value-compact">
                  {drawerData.can_manage_zatca ? (
                    <Tag color="green">{t("yes")}</Tag>
                  ) : (
                    <Tag color="red">{t("no")}</Tag>
                  )}
                </div>
              </Col>
            )}
          </Row>
        )}
      </Drawer>

      {/* Add License Modal */}
      {activeSubscription && (
        <Modal
          open={addLicenseModalVisible}
          title={t("add_license")}
          onOk={handleAddLicense}
          onCancel={() => setAddLicenseModalVisible(false)}
          footer={[
            <Button
              type="default"
              key="back"
              loading={loading}
              onClick={() => setAddLicenseModalVisible(false)}
            >
              {t("cancel")}
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleAddLicense}
            >
              {t("add_license")}
            </Button>,
          ]}
        >
          <Form
            name="add-license-form"
            layout="vertical"
            onFinish={handleAddLicense}
            className="app-form-full"
            form={addLicenseForm}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                columnGap: "20px",
                marginTop: "20px",
                marginBottom: "20px",
              }}
              className="users_count"
            >
              <QuantityStepper
                value={usersCount}
                min={1}
                decreaseDisabled={usersCount <= 1}
                onDecrease={() =>
                  setUsersCount((count) => Math.max(1, count - 1))
                }
                onIncrease={() => setUsersCount((count) => count + 1)}
              />
              <span>{t("licenses")}</span>
            </div>
          </Form>
        </Modal>
      )}

      {invoice && (
        <HyperPayPaymentModal
          invoiceId={invoice.id}
          amount={invoice.amount_charged}
          paymentModalVisible={paymentModalVisible}
          setPaymentModalVisible={setPaymentModalVisible}
        />
      )}
    </>
  );
};

export default Employees;
